import { useMemo, useRef, useState } from "react";
import { ColDef, ColumnState } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { GridContextWrapper } from "src/components/common/gridWrapper/GridContextWrapper";
import Grid_Select from "src/components/common/gridWrapper/Grid_Select";
import { PDA1070_R01BomLotDto } from "./PDA1070_R01Dto";
import usePDA1070_R01Store from "./usePDA1070_R01Store";
import { aggridHeaderClass, pdaCellStyle } from "src/components/common/common";

type BomLotRow = PDA1070_R01BomLotDto & { inserted?: boolean; isScanned?: boolean };

const PDA1070_R01DetailTable = () => {
  const { workOrderDetail, selectedBomLots, setScannedBomLot, setLotNo } = usePDA1070_R01Store();
  const gridRef = useRef<AgGridReact<BomLotRow>>(null);
  const gridWrapperRef = useRef<HTMLDivElement>(null);
  const [hoveredRowIndex, setHoveredRowIndex] = useState<number | null>(null);
  const [columnState, setColumnState] = useState<ColumnState[] | null>(null);

  const bomLots = workOrderDetail?.bomLots ?? [];

  /** 품목코드별 스캔된 LOT 목록 */
  const lotNoByItemCd = useMemo(() => {
    const map = new Map<string, string[]>();
    selectedBomLots.forEach((s) => {
      if (!map.has(s.itemCd)) map.set(s.itemCd, []);
      map.get(s.itemCd)!.push(s.lotNo);
    });
    return map;
  }, [selectedBomLots]);

  /** 품목코드별 스캔된 LOT 수량 합산 */
  const qtyByItemCd = useMemo(() => {
    const map = new Map<string, number>();
    selectedBomLots.forEach((s) => {
      map.set(s.itemCd, (map.get(s.itemCd) ?? 0) + s.shipQty);
    });
    return map;
  }, [selectedBomLots]);

  /** 스캔된 품목 itemCd 집합 */
  const scannedItemCds = useMemo(
    () => new Set(selectedBomLots.map((s) => s.itemCd)),
    [selectedBomLots]
  );

  /**
   * 검수(스캔)된 행을 맨 위로 정렬 (PDA1120_R01 방식과 동일)
   */
  const sortedBomLots = useMemo<BomLotRow[]>(() => {
    return [...bomLots]
      .map((row) => ({
        ...row,
        isScanned: scannedItemCds.has(row.itemCd),
      }))
      .sort((a, b) => {
        if (a.isScanned && !b.isScanned) return -1;
        if (!a.isScanned && b.isScanned) return 1;
        return 0;
      });
  }, [bomLots, scannedItemCds]);

  const columnDefs: ColDef<BomLotRow>[] = useMemo(
    () => [
      {
        headerName: "품명",
        field: "itemNm",
        valueGetter: (params) => params.data?.itemNm ?? "-",
        cellStyle: (params) => ({
          textAlign: "left",
          ...pdaCellStyle,
          backgroundColor: params.data?.isScanned ? "#fff3cd" : "white",
          fontWeight: params.data?.isScanned ? "bold" : "normal",
        }),
        headerClass: aggridHeaderClass,
        flex: 2,
        minWidth: 100,
      },
      {
        headerName: "위치",
        field: "locationCd",
        valueGetter: (params) => {
          const v = params.data?.locationCd;
          return v && String(v).trim() !== "" ? v : "-";
        },
        cellStyle: (params) => ({
          textAlign: "left",
          ...pdaCellStyle,
          backgroundColor: params.data?.isScanned ? "#fff3cd" : "white",
          fontWeight: params.data?.isScanned ? "bold" : "normal",
        }),
        headerClass: aggridHeaderClass,
        flex: 1,
        minWidth: 90,
      },
      {
        headerName: "LOT",
        field: "lotNo",
        valueGetter: (params) => {
          const itemCd = params.data?.itemCd;
          const scanned = itemCd ? lotNoByItemCd.get(itemCd) : undefined;
          if (scanned?.length) return scanned.join(", ");
          return params.data?.lotNo ?? "-";
        },
        cellStyle: (params) => ({
          textAlign: "left",
          ...pdaCellStyle,
          backgroundColor: params.data?.isScanned ? "#fff3cd" : "white",
          fontWeight: params.data?.isScanned ? "bold" : "normal",
        }),
        headerClass: aggridHeaderClass,
        flex: 1.2,
        minWidth: 100,
      },
      {
        headerName: "수량",
        field: "lotQty",
        valueGetter: (params) => {
          const itemCd = params.data?.itemCd;
          if (!itemCd) return "-";

          const scannedQty = qtyByItemCd.get(itemCd);
          if (scannedQty !== undefined) return scannedQty.toLocaleString();

          const lotQty = params.data?.lotQty;
          return lotQty != null ? Number(lotQty).toLocaleString() : "-";
        },
        cellStyle: (params) => ({
          textAlign: "right",
          ...pdaCellStyle,
          backgroundColor: params.data?.isScanned ? "#fff3cd" : "white",
          fontWeight: params.data?.isScanned ? "bold" : "normal",
        }),
        headerClass: aggridHeaderClass,
        flex: 1,
        minWidth: 90,
      },
    ],
    [lotNoByItemCd, qtyByItemCd]
  );

  const handleRowClicked = (bomLot: PDA1070_R01BomLotDto) => {
    if (!bomLot?.lotNo) return;
    setScannedBomLot(bomLot);
    setLotNo(bomLot.lotNo);
  };

  const initSelected = () => {};

  if (!workOrderDetail) {
    return (
      <div className="flex-1 p-3 pt-0 flex w-full h-full flex-col overflow-hidden">
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center text-gray-500">
            <p className="text-lg mb-2">📦</p>
            <p>작업지시를 선택하세요</p>
          </div>
        </div>
      </div>
    );
  }

  if (bomLots.length === 0) {
    return (
      <div className="flex-1 p-3 pt-0 flex w-full h-full flex-col overflow-hidden">
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center text-gray-500">
            <p className="text-lg mb-2">📦</p>
            <p>출고할 자재가 없습니다</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 p-3 pt-0 flex flex-col overflow-hidden min-h-0">
      <GridContextWrapper<BomLotRow>
        gridRef={gridRef}
        gridWrapperRef={gridWrapperRef}
        paddedGridData={sortedBomLots}
        initSelected={initSelected}
        baseColumnDefs={columnDefs}
        setColumnState={setColumnState}
        localStorageKey="PDA1070_R01_DetailTable_ColumnState"
        enableDataHistory={false}
      >
        <div className="flex-1 w-full h-full min-h-0">
          <Grid_Select<BomLotRow>
            ref={gridRef}
            rowData={sortedBomLots}
            columnDefs={columnDefs}
            rowSelection="single"
            onRowClicked={(event) => {
              if (event.data) handleRowClicked(event.data);
            }}
            setHoveredRowIndex={setHoveredRowIndex}
            columnStateKey="PDA1070_R01_DetailTable_ColumnState"
            setColumnState={setColumnState}
          />
        </div>
      </GridContextWrapper>
    </div>
  );
};

export default PDA1070_R01DetailTable;
